import { IonList } from "@ionic/react";
import { useCallback } from "react";

import styles from "@components/fields/RadioButtonGroup.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import { generateHTMLId } from "@utils/htmlUtils";

export type OptionType = {
  label: string;
  value: string | number;
};

type Props<Option> = {
  currentSelection: string | number;
  fullWidth?: boolean;
  onChange: (value: Option) => void;
  options: Option[];
};

const RadioButtonGroup = <Option extends OptionType>({
  currentSelection,
  fullWidth,
  onChange,
  options
}: Props<Option>) => {
  const generateChangeHandler = useCallback(
    (option: Option) => () => {
      onChange(option);
    },
    [onChange]
  );

  return (
    <fieldset className={styles.radioGroup}>
      <div className={styles.options}>
        <IonList className={styles.list}>
          {options.map(option => {
            const id = `${option.value}-${generateHTMLId()}`;
            return (
              <div
                className={`${option.value === currentSelection ? styles.checked : styles.notChecked} ${
                  fullWidth && styles.fullWidth
                }`}
                data-test="radio-option"
                key={`${option.label}-${option.value ?? "null"}`}
              >
                <input
                  aria-label={option.label ?? undefined}
                  checked={option.value === currentSelection}
                  className={styles.input}
                  id={id}
                  onChange={generateChangeHandler(option)}
                  type="radio"
                />
                <label className={styles.groupLabel} htmlFor={id}>
                  <div className={styles.imageAndLabel}>
                    <div className={styles.optionLabel}>
                      <BodyCopy color={option.value === currentSelection ? "white" : "gray-300"}>
                        {option.label}
                      </BodyCopy>
                    </div>
                  </div>
                </label>
              </div>
            );
          })}
        </IonList>
      </div>
    </fieldset>
  );
};

export default RadioButtonGroup;
